// A set of utils to help work with graphql edges and nodes, a common pattern when
// dealing with graphql data from our API!

import { useMemo } from 'react';
import useEvent from 'shared/hooks/useEvent';

/**
 * Takes a graphql edge array and creates an iterator that yields each non-null node in the array.
 * @param edges - Array of edges to iterate over
 */
export function* edgeNodeIterator<
  TEdge extends { node?: object | null | undefined },
  TNode = NonNullable<TEdge['node']>,
>(edges: Array<TEdge | null> | null | undefined): IterableIterator<TNode> {
  if (!edges) {
    // If edges is null or undefined, return an empty array
    return;
  }

  for (let i = 0; i < edges.length; i++) {
    const item = edges[i];

    if (item?.node) {
      yield item.node as TNode;
    }
  }
}

/**
 * Takes a graphql edge array and returns an array of non-null nodes from those edges.
 * This helps make it more pleasant to work around the fact that graphql edges and nodes
 * are typed as nullable.
 */
export const getEdgeNodes = <
  TEdge extends { node?: object | null | undefined },
  TNode = NonNullable<TEdge['node']>,
>(
  edges: Array<TEdge | null> | null | undefined,
): TNode[] => Array.from(edgeNodeIterator(edges));

/**
 * Hook version of getEdgeNodes; takes a graphql edge array and returns an array of non-null nodes from those edges.
 */
export const useEdgeNodes = <
  TEdge extends { node?: object | null | undefined },
  TNode = NonNullable<TEdge['node']>,
>(
  edges: Array<TEdge | null> | null | undefined,
): TNode[] => useMemo(() => getEdgeNodes(edges), [edges]);

/**
 * Takes a graphql edge array and runs a callback on each non-null node in the array.
 */
export const forEachNodeInEdges = <
  TEdge extends { node?: object | null | undefined },
  TNode = NonNullable<TEdge['node']>,
>(
  edges: Array<TEdge | null> | null | undefined,
  callback: (node: TNode, index: number) => void,
): void => {
  let i = 0;

  for (const node of edgeNodeIterator(edges)) {
    callback(node as TNode, i);
    i += 1;
  }
};

/**
 * Takes a graphql edge array, runs a transform callback on each non-null node in the array,
 * and returns an array of the transformed results.
 */
export const mapEdgeNodes = <
  TMapResult,
  TEdge extends { node?: object | null | undefined },
  TNode = NonNullable<TEdge['node']>,
>(
  edges: Array<TEdge | null> | null | undefined,
  transformCallback: (node: TNode, index: number) => TMapResult,
): TMapResult[] => {
  const transformedArray = new Array<TMapResult>(edges?.length ?? 0);

  let i = 0;
  for (const node of edgeNodeIterator(edges)) {
    transformedArray[i] = transformCallback(node as TNode, i);
    i += 1;
  }

  // Trim the array to the number of items we actually transformed
  transformedArray.length = i;

  return transformedArray;
};

/**
 * Hook version of mapEdgeNodes; takes a graphql edge array, runs a transform callback on each non-null node in the array,
 * and returns an array of the transformed results.
 */
export const useMappedEdgeNodes = <
  TMapResult,
  TEdge extends { node?: object | null | undefined },
  TNode = NonNullable<TEdge['node']>,
>(
  edges: Array<TEdge | null> | null | undefined,
  transformCallback: (node: TNode, index: number) => TMapResult,
): TMapResult[] => {
  // Stabilize the reference to the transform callback so we don't re-run the `useMemo` hook
  // unless the edges array has changed.
  const stableTransformCallback = useEvent(transformCallback);

  return useMemo(
    () => mapEdgeNodes(edges, stableTransformCallback),
    [edges, stableTransformCallback],
  );
};

export const filterEdgeNodes = <
  TEdge extends { node?: object | null | undefined },
  TNode = NonNullable<TEdge['node']>,
>(
  edges: Array<TEdge | null> | null | undefined,
  filterCallback: (node: TNode, index: number) => boolean,
): TNode[] => {
  const filteredArray: TNode[] = [];

  let i = 0;
  for (const node of edgeNodeIterator(edges)) {
    if (filterCallback(node as TNode, i)) {
      filteredArray.push(node as TNode);
    }
    i += 1;
  }

  return filteredArray;
};

export const useFilteredEdgeNodes = <
  TEdge extends { node?: object | null | undefined },
  TNode = NonNullable<TEdge['node']>,
>(
  edges: Array<TEdge | null> | null | undefined,
  filterCallback: (node: TNode, index: number) => boolean,
): TNode[] => {
  // Stabilize the reference to the filter callback so we don't re-run the `useMemo` hook
  // unless the edges array has changed.
  const stableFilterCallback = useEvent(filterCallback);

  return useMemo(() => filterEdgeNodes(edges, stableFilterCallback), [edges, stableFilterCallback]);
};
